import React, { useState, useEffect, useCallback } from 'react';

import styled from 'styled-components';
import { v4 as uuidV4 } from 'uuid';

import * as analytics from '@adsk/acs-web-analytics';
import Button from '@adsk/alloy-react-button';
import {
  AlertInformationIcon,
  ISpeechBubbleIcon,
} from '@adsk/alloy-react-icon';
import Panel from '@adsk/alloy-react-panel';
import theme from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

import DocsUploadBody from './DocsUpload';
import { UPLOAD_STATUS } from './helpers/consts';
import { FileUtil } from './helpers/sdkServices';
import {
  validateFiles,
  UploadWorker,
  getUploadFileEntity,
  isRfiSource,
} from './helpers/uploadHelper';
import { useTranslation } from './i18n';

const { INITIALIZING, UPLOAD_FAILED, PROCESSING, PROCESS_FAILED } =
  UPLOAD_STATUS;

const UploadButton = styled(Button)`
  width: 100%;
`;

let uploadWorker;
let filesStore = []; // Callback function closure will prevent to get the latest files via useState

const UploadPanel = ({
  open,
  projectId,
  parentFolderUrn,
  sourceEntityType,
  floatingPanel,
  onCreateSuccess,
  onClose,
}) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [toUploadFiles, setToUploadFiles] = useState([]);
  const isRfi = isRfiSource(sourceEntityType);
  const AlertIcon = isRfi ? ISpeechBubbleIcon : AlertInformationIcon;

  /**
   * cancel upload has three specifics:
   * 1 cancel during uploading to OSS: just remove file from upload state
   * 2 cancel during uploaded to OSS and before sending uploadCommand
   * 3 cancel after sending uploadCommand: send cancelCommand
   *
   * This `cancelUpload` method only handles specific 1
   */
  const cancelUpload = (file) => uploadWorker && uploadWorker.removeFile(file);

  const handleRemove = (file) => {
    cancelUpload(file);
    const updatedFiles = files.filter(({ id }) => id !== file.id);
    filesStore = updatedFiles;
    setFiles(updatedFiles);
  };

  const clearContext = () => {
    filesStore = [];
    setFiles([]);
    setToUploadFiles([]);
  };

  const handleClose = () => {
    files.forEach(cancelUpload);
    clearContext();

    onClose();
  };

  const checkFileRemoved = (file) =>
    !filesStore.find(({ id }) => id === file.id);

  const handleUploadStatus = useCallback((targetFile) => {
    if (!checkFileRemoved(targetFile)) {
      const nextFiles = filesStore.map((file) =>
        targetFile.id === file.id ? targetFile : file,
      );
      filesStore = nextFiles;
      setFiles(nextFiles);
    }
  }, []);

  const handleFilesUpload = async (selectedFiles) => {
    selectedFiles = [...selectedFiles]; //FileList type to Array type
    // Workaround for filter folder: empty file type and empty extension
    const pureFiles = selectedFiles.filter(
      ({ type, name }) => type || FileUtil.getFileExtension(name),
    );

    if (pureFiles.length === 0) {
      return;
    }

    const [validFiles] = await validateFiles(pureFiles);
    selectedFiles = pureFiles.map((file) => ({
      id: uuidV4(),
      parentFolderUrn,
      ref: file,
      status: INITIALIZING,
      progress: 0,
      isValid: !!validFiles.find(({ name }) => name === file.name),
    }));
    const toUploadFiles = selectedFiles.filter(({ isValid }) => isValid);
    filesStore = [...files, ...selectedFiles];

    setFiles(filesStore);
    setToUploadFiles(toUploadFiles);

    analytics.track('document.upload.initiate_from_picker', {
      'File Number Per Batch': toUploadFiles.length,
    });
  };

  //enable when all files uploaded and command dispatched. failed files are not be passed to outside
  const checkDisabled = () => {
    if (files.length === 0) {
      return true;
    }

    return !files.every(({ status }) =>
      [UPLOAD_FAILED, PROCESSING, PROCESS_FAILED].includes(status),
    );
  };

  useEffect(() => {
    if (toUploadFiles.length > 0) {
      if (!uploadWorker) {
        uploadWorker = new UploadWorker(projectId, toUploadFiles, {
          uploadStatusHandler: handleUploadStatus,
          checkFileRemoved,
        });
      } else {
        uploadWorker.addFiles(toUploadFiles);
      }
    }

    return () => {
      uploadWorker = null;
    };
  }, [projectId, toUploadFiles, handleUploadStatus]);

  const handleCompleteClick = () => {
    const uploadedFiles = files.filter(({ status }) => PROCESSING === status);
    const entities = uploadedFiles.map(({ result }) =>
      getUploadFileEntity(result, parentFolderUrn, isRfi),
    );
    filesStore = [];
    onCreateSuccess(entities);
  };
  const title = (
    <div style={{ display: 'flex', whiteSpace: 'nowrap' }}>
      <span>{t('UPLOAD_TITLE')}</span>
      <Tooltip
        style={{ maxWidth: '180px' }}
        content={t(
          isRfi ? 'UPLOAD_FILES_TO_RFI_TOOLTIP' : 'UPLOAD_FILES_TOOLTIP',
        )}
      >
        <AlertIcon
          style={{
            margin: '2px 0 0 6px',
            color: theme.colors.charcoal700,
          }}
          size={20}
        />
      </Tooltip>
    </div>
  );
  return (
    <Panel
      open={open}
      floating={floatingPanel}
      renderHeader={() => <Panel.Header title={title} onClose={handleClose} />}
      renderFooter={({ style }) => (
        <div style={style}>
          <UploadButton
            variant={Button.VARIANTS.PRIMARY}
            size={Button.SIZES.LARGE}
            disabled={checkDisabled()}
            onClick={handleCompleteClick}
          >
            {t(isRfi ? 'UPLOAD_TO_RFI' : 'UPLOAD')}
          </UploadButton>
        </div>
      )}
    >
      <Panel.Body>
        <DocsUploadBody
          files={files}
          onUpload={handleFilesUpload}
          onRemove={handleRemove}
        />
      </Panel.Body>
    </Panel>
  );
};

UploadPanel.displayName = 'UploadPanel';

export default UploadPanel;
